/**
 * Reusable ICD-10 Code Search Component
 * Global component for all CounselCore platform organizations
 * Use in: Treatment Plans, Progress Notes, Sessions, Assessments, Claims, etc.
 */

import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { ICD10_MENTAL_HEALTH_CODES, searchICD10Codes, getAllICD10Codes } from '../data/icd10Codes';

const ICD10CodeSearch = ({ 
  value = '',
  onChange,
  onSelect,
  placeholder = "Search: depression, F33, anxiety...",
  className = "",
  showCategoryBrowser = true,
  autofillDiagnosis = false,
  label = "ICD-10 Code (Searchable)"
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredCodes, setFilteredCodes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = React.useRef(null);
  const inputRef = React.useRef(null);

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
    setHighlightedIndex(-1);
    
    if (term.length > 0) {
      const results = searchICD10Codes(term);
      setFilteredCodes(results.slice(0, 15)); // Show top 15 results
      setShowDropdown(true);
      setSelectedCategory(null);
    } else {
      setShowDropdown(false);
      setFilteredCodes([]);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || filteredCodes.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredCodes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCodes.length) {
          handleSelectCode(filteredCodes[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedEl = dropdownRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Handle category browsing
  const handleBrowseCategory = (category) => {
    setSelectedCategory(category);
    setFilteredCodes(category.codes);
    setShowDropdown(true);
    setSearchTerm('');
  };

  // Handle code selection
  const handleSelectCode = (item) => {
    // Call the onSelect callback with code and description
    if (onSelect) {
      onSelect({
        code: item.code,
        description: item.description,
        category: item.category
      });
    }
    
    // Call onChange if provided (for controlled component)
    if (onChange) {
      onChange(item.code);
    }
    
    // Clear search and close dropdown
    setSearchTerm('');
    setShowDropdown(false);
    setSelectedCategory(null);
  };

  // Clear selection
  const handleClear = () => {
    setSearchTerm('');
    setShowDropdown(false);
    setSelectedCategory(null);
    if (onChange) {
      onChange('');
    }
    if (onSelect) {
      onSelect({ code: '', description: '', category: '' });
    }
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium mb-2">{label}</label>
      )}
      
      <div className="relative">
        {/* Search Input */}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm || value}
          onChange={(e) => handleSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchTerm && filteredCodes.length > 0) {
              setShowDropdown(true);
            }
          }}
          onBlur={(e) => {
            // Don't close if clicking within dropdown
            if (dropdownRef.current && dropdownRef.current.contains(e.relatedTarget)) {
              return;
            }
            // Longer delay to allow click on dropdown items
            setTimeout(() => setShowDropdown(false), 300);
          }}
          className="w-full p-2 pr-16 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder={placeholder}
        />
        
        {/* Icons */}
        <div className="absolute right-2 top-2.5 flex items-center gap-1">
          {(value || searchTerm) && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded"
              title="Clear"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
          <Search className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Current Selection Display */}
      {value && !searchTerm && (
        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="default" className="font-mono">
              {value}
            </Badge>
            <span className="text-sm text-gray-700">Selected</span>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Change
          </button>
        </div>
      )}

      {/* Category Browser */}
      {showCategoryBrowser && !showDropdown && !value && (
        <div className="mt-2">
          <div className="text-xs text-gray-600 mb-2">Or browse by category:</div>
          <div className="flex flex-wrap gap-2">
            {ICD10_MENTAL_HEALTH_CODES.slice(0, 6).map((category, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleBrowseCategory(category)}
                className="text-xs px-2 py-1 bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-700 rounded border border-gray-300 hover:border-blue-400 transition-colors"
              >
                {category.category}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setFilteredCodes(getAllICD10Codes().slice(0, 20));
                setShowDropdown(true);
              }}
              className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
            >
              View All →
            </button>
          </div>
        </div>
      )}

      {/* Dropdown Results */}
      {showDropdown && filteredCodes.length > 0 && (
        <div 
          ref={dropdownRef}
          tabIndex="-1"
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-y-auto"
        >
          {/* Category Header */}
          {selectedCategory && (
            <div className="sticky top-0 bg-blue-600 text-white p-3 flex items-center justify-between">
              <div>
                <div className="font-semibold">{selectedCategory.category}</div>
                <div className="text-xs opacity-90">{selectedCategory.codes.length} codes available</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory(null);
                  setShowDropdown(false);
                }}
                className="text-white hover:bg-blue-700 p-1 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Search Results Header */}
          {!selectedCategory && searchTerm && (
            <div className="sticky top-0 bg-gray-100 px-3 py-2 border-b">
              <div className="text-xs text-gray-600">
                Found {filteredCodes.length} result{filteredCodes.length !== 1 ? 's' : ''} for &quot;{searchTerm}&quot;
                <span className="ml-2 text-gray-400">(↑↓ to navigate, Enter to select)</span>
              </div>
            </div>
          )}

          {/* Code List */}
          {filteredCodes.map((item, index) => (
            <div
              key={index}
              data-index={index}
              onClick={() => handleSelectCode(item)}
              className={`p-3 border-b last:border-b-0 cursor-pointer transition-colors ${
                index === highlightedIndex ? 'bg-blue-100' : 'hover:bg-blue-50'
              }`}
            >
              <div className="flex items-start gap-3">
                <Badge 
                  variant="outline" 
                  className={`text-xs font-mono shrink-0 bg-white border-2 font-bold ${
                    index === highlightedIndex ? 'border-blue-600 text-blue-800' : 'border-blue-400 text-blue-700'
                  }`}
                >
                  {item.code}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 leading-tight">
                    {item.description}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                    {item.category}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results */}
      {showDropdown && filteredCodes.length === 0 && searchTerm && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-4 text-center">
          <div className="text-gray-500 text-sm">
            No codes found for &quot;{searchTerm}&quot;
          </div>
          <button
            type="button"
            onClick={() => {
              setFilteredCodes(getAllICD10Codes().slice(0, 20));
              setSearchTerm('');
            }}
            className="mt-2 text-xs text-blue-600 hover:text-blue-800"
          >
            Browse all codes instead
          </button>
        </div>
      )}
    </div>
  );
};

export default ICD10CodeSearch;
